import React from "react"
import { observer } from "mobx-react-lite"

import type { Token } from "@/domains/core"
import type { IVenammStore, VenammSellState, VenammSellStore } from "@/domains/venamm"
import { Card } from "@/components/card"
import { cn } from "@/domains/library"
import { VenammStateButtonsMap } from "@/domains/lock-page/components/card/venamm-card/composables/venamm-buttons/venamm-state-buttons-map"
import { VenammStateComponentMap } from "@/domains/lock-page/components/card/venamm-card/composables/venamm-panels/venamm-state-component-map"
import { VenammProgressIndicator } from "@/domains/lock-page/components/card/venamm-card/composables/venamm-steps/venamm-progress-indicator"

import { VenammVeDetails } from "../composables/venamm-panels/venamm-ve-details"

function formatStateName(state: VenammSellState): string {
  return state
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

export interface VenammCardProps {
  store: VenammSellStore
  governanceToken: Token
  vestedToken: Token
  venammStore: IVenammStore
  icon?: React.ReactElement
  onCancel: () => void
  onStartSell: () => void
  onReset: () => void
  onRetryReset: () => void
  onRetryQuote: () => void
  onApprove: () => void
  onRetryApprove: () => void
  onSell: () => void
  onRetrySell: () => void
}

export const VenammCard = observer(
  ({
    store,
    governanceToken,
    vestedToken,
    venammStore,
    icon,
    onCancel,
    onStartSell,
    onReset,
    onRetryReset,
    onRetryQuote,
    onApprove,
    onRetryApprove,
    onSell,
    onRetrySell,
  }: VenammCardProps) => {
    if (!store) {
      console.error("VenammCard: store is undefined")
      return null
    }

    return (
      <Card.Container
        className={cn("flex w-full flex-col gap-6", "rounded-lg border border-p400-light bg-p400 p-4 lg:p-6")}
      >
        <Card.Header>
          {icon && (
            <Card.HeaderIconContainer>
              {React.cloneElement(icon, { className: "text-t200", size: 24 })}
            </Card.HeaderIconContainer>
          )}

          <div className="flex w-full flex-col">
            <Card.HeaderTitle>
              Venamm{store.state !== "idle" ? ` - ${formatStateName(store.state)}` : ""}
            </Card.HeaderTitle>
            <Card.HeaderDescription>Sell your veSCALE for liquid tokens</Card.HeaderDescription>
          </div>
        </Card.Header>

        <VenammProgressIndicator store={store} className="mb-4" />

        {store.lock && (
          <div className="mb-4 rounded-md border border-p300-shade bg-p400-light p-2 px-4">
            <VenammVeDetails lock={store.lock} governanceToken={governanceToken} vestedToken={vestedToken} />
          </div>
        )}

        <div className="mb-4">
          <VenammStateComponentMap store={store} governanceToken={governanceToken} venammStore={venammStore} />
        </div>

        <VenammStateButtonsMap
          store={store}
          onCancel={onCancel}
          onStartSell={onStartSell}
          onReset={onReset}
          onRetryReset={onRetryReset}
          onRetryQuote={onRetryQuote}
          onApprove={onApprove}
          onRetryApprove={onRetryApprove}
          onSell={onSell}
          onRetrySell={onRetrySell}
        />
      </Card.Container>
    )
  },
)
